// location-mapping/location-mapping.component.ts

import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule, NgIf } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { forkJoin } from 'rxjs';

import { SharedService } from '../services/shared.service';

import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule, MatSelectChange } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { CustomizerSettingsService } from '../customizer-settings/customizer-settings.service';
import { ToastrService } from 'ngx-toastr';

// ── Models ────────────────────────────────────────────────────────────────────

interface LocationMapping {
  id: number;
  lw_location_id:   string | null;
  lw_location_name: string | null;
  se_location_id:   string | null;
  se_location_name: string | null;
  is_active:        number;
  is_combination:   number;
  ignored:          number;
  mapping_source:   string | null;
  mapped_by:        string | null;
  mapped_at:        string | null;
  notes:            string | null;
  created_at:       string | null;
}

interface LiveLocation { id: string; name: string; }

interface Alert {
  id: number; side: string;
  lw_location_id: string | null; lw_location_name: string | null;
  se_location_id: string | null; se_location_name: string | null;
  detected_at: string; is_resolved: number; notes: string | null;
}

interface AuditEntry {
  id: number; action: string; changed_by: string | null; changed_at: string;
  old_lw_location_name: string | null; old_se_location_name: string | null;
  old_is_active: number | null;
  new_lw_location_name: string | null; new_se_location_name: string | null;
  new_is_active: number | null; notes: string | null;
}

interface SyncGroup {
  se_location_id: string; se_location_name: string;
  is_combination: boolean;
  lw_locations: { id: string; name: string }[];
}

interface LocationMappingStats {
  active_count:           number;
  pending_count:          number;
  ignored_count:          number;
  combination_count:      number;
  unresolved_alert_count: number;
}

interface SyncPreview {
  will_sync: SyncGroup[];
  active_warehouse_count: number; active_sku_count: number;
  pending_mapping_count: number; ignored_location_count: number;
  estimated_sync_operations: number; ready_to_sync: boolean;
  // combination_warehouses can be absent or undefined from the API —
  // always access via the safe getter below, never directly in templates.
  combination_warehouses?: string[];
}

// ── Component ─────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-location-mapping',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    NgIf,
    FormsModule,
    MatCardModule,
    MatButtonModule,
    MatTableModule,
    MatTabsModule,
    MatTooltipModule,
    MatSelectModule,
    MatCheckboxModule,
    MatIconModule,
    MatInputModule,
    MatFormFieldModule,
    MatExpansionModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './location-mapping.component.html',
  styleUrls: ['./location-mapping.component.scss'],
})
export class LocationMappingComponent implements OnInit {

  private readonly API = '/location_mappings';

  // Theme (matching admin.component pattern)
  isToggled    = false;
  isDarkTheme  = false;

  // State
  loading         = false;
  saving          = false;
  liveDataLoading = false;
  showSyncPreview = false;
  showSyncStatus  = false;

  // Data
  allMappings:  LocationMapping[] = [];
  unmappedLw:   any[] = [];
  unmappedSe:   any[] = [];
  totalUnmapped: number = 0;
  alerts:       Alert[] = [];
  syncPreview:  SyncPreview | null = null;
  stats:        LocationMappingStats | null = null;
  auditRows:    AuditEntry[] = [];
  auditMapping: LocationMapping | null = null;
  lwLocations:  LiveLocation[] = [];
  seWarehouses: LiveLocation[] = [];
  pagination:   any = {};

  // Mat table data source
  mappingsDataSource = new MatTableDataSource<LocationMapping>([]);
  mappingDisplayedColumns: string[] = [
    'lw_location', 'arrow', 'se_location', 'status', 'type', 'source', 'actions'
  ];

  // Filters & pagination
  filterStatus: 'all' | 'active' | 'inactive' | 'ignored' = 'all';
  page  = 1;
  limit = 20;

  // Create form
  showCreateForm = false;
  createForm = {
    lw_location_id: '', lw_location_name: '',
    se_location_id: '', se_location_name: '',
    is_combination: false, activate: true, notes: '',
  };

  // Edit
  editingId: number | null = null;
  editForm: Partial<LocationMapping> = {};

  // Tab control — bound to [selectedIndex] on mat-tab-group
  selectedTabIndex = 0;

  // Scroll anchor for the create form panel
  @ViewChild('createFormAnchor') createFormAnchor!: ElementRef;

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router,
    public  themeService: CustomizerSettingsService,
    private toastr: ToastrService,
    private sharedService: SharedService,
  ) {
    this.themeService.isToggled$.subscribe(v => this.isToggled = v);
    this.themeService.isDarkThemeEnabled$.subscribe(v => this.isDarkTheme = v);
  }

  ngOnInit(): void { this.loadAll(); }

  // ── Safe getter — FIXES TS2532 ────────────────────────────────────────────
  // The template binds to this instead of syncPreview.combination_warehouses
  // directly, so Angular's strict null checks never see a possibly-undefined value.
  get combinationWarehouseNames(): string[] {
    return this.syncPreview?.combination_warehouses ?? [];
  }

  // ── Load ──────────────────────────────────────────────────────────────────

  /**
   * Maps the UI's filter buttons (All / Active / Pending / Ignored) onto the
   * `is_active` / `ignored` query params that GET /all already supports.
   * Returns '' for 'all' — no filter params, every LW-anchored row shown.
   */
  private buildFilterQuery(): string {
    switch (this.filterStatus) {
      case 'active':   return '&is_active=1&ignored=0';
      case 'inactive': return '&is_active=0&ignored=0';
      case 'ignored':  return '&ignored=1';
      default:         return '';
    }
  }

  loadAll(): void {
    this.loading = true;
    const filterQuery = this.buildFilterQuery();
    forkJoin({
      mappings: this.sharedService.getRequest(`${this.API}/all?limit=${this.limit}&page=${this.page}${filterQuery}`),
      unmapped: this.sharedService.getRequest(`${this.API}/unmapped`),
      alerts:   this.sharedService.getRequest(`${this.API}/alerts?is_resolved=0`),
      preview:  this.sharedService.getRequest(`${this.API}/sync-preview`),
      stats:    this.sharedService.getRequest(`${this.API}/stats`),
    }).subscribe({
      next: ({ mappings, unmapped, alerts, preview, stats }: any) => {
        this.allMappings  = mappings.content ?? [];
        this.pagination   = mappings.pagination ?? {};
        this.unmappedLw   = unmapped.unmapped_lw ?? [];
        this.unmappedSe   = unmapped.unmapped_se ?? [];
        this.totalUnmapped = unmapped.total_unmapped ?? (this.unmappedLw.length + this.unmappedSe.length);
        this.alerts       = alerts as Alert[];
        this.syncPreview  = preview as SyncPreview;
        this.stats        = stats as LocationMappingStats;
        this.refreshDataSource();
        this.loading = false;
      },
      error: (err) => { this.loading = false; this.toastr.error(this.extractError(err)); this.handleSessionExpiry(err); }
    });
  }

  loadLiveData(): void {
    if (this.lwLocations.length && this.seWarehouses.length) return;
    this.liveDataLoading = true;
    forkJoin({
      lw: this.sharedService.getRequest(`${this.API}/live-lw-locations`),
      se: this.sharedService.getRequest(`${this.API}/live-se-warehouses`),
    }).subscribe({
      next: ({ lw, se }: any) => {
        this.lwLocations = lw as LiveLocation[]; this.seWarehouses = se as LiveLocation[]; this.liveDataLoading = false;
      },
      error: (err) => { this.liveDataLoading = false; this.toastr.error(this.extractError(err)); this.handleSessionExpiry(err); }
    });
  }

  private refreshDataSource(): void {
    this.mappingsDataSource = new MatTableDataSource(this.filteredMappings);
  }

  /**
   * Switching the Show: All/Active/Pending/Ignored filter now triggers a
   * fresh server call (via loadAll(), which reads filterStatus through
   * buildFilterQuery()) instead of re-slicing whatever page happened to
   * already be loaded client-side. This fixes two problems at once:
   *  - the filtered count used to be wrong whenever the current page
   *    didn't happen to contain every matching row
   *  - pagination now reflects the TOTAL matching the filter, not the
   *    total across all statuses
   * Always reset to page 1 — page 3 of "All" is not a meaningful page
   * number once you've switched to "Ignored".
   */
  setFilter(status: 'all' | 'active' | 'inactive' | 'ignored'): void {
    this.filterStatus = status;
    this.page = 1;
    this.loadAll();
  }

  // ── Computed ──────────────────────────────────────────────────────────────

  /**
   * GET /all now applies both the lw_location_id filter AND the
   * active/pending/ignored status filter server-side (see buildFilterQuery()
   * and the repository's WHERE clause), so `allMappings` already IS the
   * correctly-filtered, correctly-paginated set. This getter is kept only
   * as a defensive belt-and-braces check — it should be a no-op filter.
   */
  get filteredMappings(): LocationMapping[] {
    return this.allMappings.filter(m => !!m.lw_location_id);
  }

  // These are sourced from GET /location_mappings/stats — a dedicated,
  // non-paginated endpoint that COUNT()s over the entire table. They used
  // to be computed by filtering `allMappings`, but that array only ever
  // holds the current page's ~20 rows, so the cards changed value every
  // time you paginated. Do not go back to filtering allMappings here.
  get activeMappingCount():   number { return this.stats?.active_count ?? 0; }
  get pendingMappingCount():  number { return this.stats?.pending_count ?? 0; }
  get combinationCount():     number { return this.stats?.combination_count ?? 0; }
  get ignoredCount():         number { return this.stats?.ignored_count ?? 0; }
  get unresolvedAlertCount(): number { return this.stats?.unresolved_alert_count ?? this.alerts.length; }

  // ── Create form ───────────────────────────────────────────────────────────

  openCreateForm(prefillLw?: any, prefillSe?: any): void {
    this.showCreateForm  = true;
    this.selectedTabIndex = 0;          // switch to All Mappings tab
    if (prefillLw) {
      this.createForm.lw_location_id   = prefillLw.lw_location_id   ?? '';
      this.createForm.lw_location_name = prefillLw.lw_location_name ?? '';
    }
    if (prefillSe) {
      this.createForm.se_location_id   = prefillSe.se_location_id   ?? '';
      this.createForm.se_location_name = prefillSe.se_location_name ?? '';
    }
    this.loadLiveData();
    // Scroll to the form after Angular renders it
    setTimeout(() => {
      this.createFormAnchor?.nativeElement?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  }

  /** mat-select emits MatSelectChange — create form LW */
  onCreateLwSelectMat(event: MatSelectChange): void {
    const f = this.lwLocations.find(l => l.id === event.value);
    if (f) { this.createForm.lw_location_id = f.id; this.createForm.lw_location_name = f.name; }
  }

  /** mat-select emits MatSelectChange — create form SE */
  onCreateSeSelectMat(event: MatSelectChange): void {
    const f = this.seWarehouses.find(w => w.id === event.value);
    if (f) { this.createForm.se_location_id = f.id; this.createForm.se_location_name = f.name; }
  }

  submitCreate(): void {
    if (!this.createForm.lw_location_id || !this.createForm.se_location_id) {
      this.toastr.error('Please select both a Linnworks location and a ShipEarly warehouse.'); return;
    }
    this.saving = true;
    this.sharedService.postRequest(`${this.API}/create`, this.createForm).subscribe({
      next: () => {
        this.saving = false; this.showCreateForm = false;
        this.createForm = { lw_location_id: '', lw_location_name: '',
          se_location_id: '', se_location_name: '', is_combination: false,
          activate: true, notes: '' };
        this.toastr.success('Mapping created successfully.');
        this.loadAll();
      },
      error: (err) => { this.saving = false; this.toastr.error(this.extractError(err)); this.handleSessionExpiry(err); }
    });
  }

  // ── Inline edit ───────────────────────────────────────────────────────────

  startEdit(m: LocationMapping): void {
    this.editingId = m.id;
    this.editForm  = { ...m };
    this.loadLiveData();
  }

  /** mat-select emits MatSelectChange — edit row LW */
  onEditLwSelectMat(event: MatSelectChange): void {
    const f = this.lwLocations.find(l => l.id === event.value);
    if (f) { this.editForm.lw_location_id = f.id; this.editForm.lw_location_name = f.name; }
  }

  /** mat-select emits MatSelectChange — edit row SE */
  onEditSeSelectMat(event: MatSelectChange): void {
    const f = this.seWarehouses.find(w => w.id === event.value);
    if (f) { this.editForm.se_location_id = f.id; this.editForm.se_location_name = f.name; }
  }

  cancelEdit(): void { this.editingId = null; this.editForm = {}; }

  saveEdit(): void {
    if (!this.editingId) return;
    this.saving = true;
    const payload = {
      id:               this.editingId,
      lw_location_id:   this.editForm.lw_location_id,
      lw_location_name: this.editForm.lw_location_name,
      se_location_id:   this.editForm.se_location_id,
      se_location_name: this.editForm.se_location_name,
      is_combination:   this.editForm.is_combination,
      notes:            this.editForm.notes,
    };
    this.sharedService.putRequest(`${this.API}/update`, payload).subscribe({
      next: () => {
        this.saving = false; this.editingId = null;
        this.toastr.success('Mapping updated.'); this.loadAll();
      },
      error: (err) => { this.saving = false; this.toastr.error(this.extractError(err)); this.handleSessionExpiry(err); }
    });
  }

  // ── Actions ───────────────────────────────────────────────────────────────

  activate(m: LocationMapping): void {
    this.postAction(m.id, 'activate', {}, 'Mapping activated — will sync on next cron run.');
  }

  deactivate(m: LocationMapping): void {
    if (!confirm(`Deactivate "${m.lw_location_name} ↔ ${m.se_location_name}"? Sync will pause.`)) return;
    this.postAction(m.id, 'deactivate', {}, 'Mapping deactivated.');
  }

  ignore(m: LocationMapping): void {
    if (!confirm(`Mark "${m.lw_location_name || m.se_location_name}" as intentionally unmapped?\nThis suppresses all alerts for this location.`)) return;
    this.postAction(m.id, 'ignore', {}, 'Location marked as ignored.');
  }

  unignore(m: LocationMapping): void {
    this.postAction(m.id, 'unignore', {}, 'Location restored to pending.');
  }

  private postAction(id: number, action: string, body: any, msg: string): void {
    this.sharedService.postRequest(`${this.API}/${id}/${action}`, body).subscribe({
      next: () => { this.toastr.success(msg); this.loadAll(); },
      error: (err) => { this.toastr.error(this.extractError(err)); this.handleSessionExpiry(err); }
    });
  }

  // NOTE: soft-delete/remove is intentionally not exposed on the front end.
  // The backend DELETE /remove endpoint still exists but is unused from
  // this component — see startEdit/ignore/unignore for the supported ways
  // to change a mapping's state instead.

  // ── Alerts ────────────────────────────────────────────────────────────────

  resolveAlert(a: Alert): void {
    this.sharedService.postRequest(`${this.API}/alerts/${a.id}/resolve`, {}).subscribe({
      next: () => { this.toastr.success('Alert resolved.'); this.loadAll(); },
      error: (err) => { this.toastr.error(this.extractError(err)); this.handleSessionExpiry(err); }
    });
  }

  // ── Audit ─────────────────────────────────────────────────────────────────

  viewAudit(m: LocationMapping): void {
    this.auditMapping = m;
    this.sharedService.getRequest(`${this.API}/${m.id}/audit`).subscribe({
      next: (res: any) => { this.auditRows = res as AuditEntry[]; },
      error: (err) => { this.toastr.error(this.extractError(err)); this.handleSessionExpiry(err); }
    });
  }

  closeAudit(): void { this.auditMapping = null; this.auditRows = []; }

  // ── Pagination ────────────────────────────────────────────────────────────

  changePage(p: number): void { this.page = p; this.loadAll(); }

  // ── Helpers ───────────────────────────────────────────────────────────────

  private extractError(err: any): string {
    return err?.error?.message?.message || err?.error?.message || err?.error?.detail || 'An error occurred.';
  }

  private handleSessionExpiry(err: any): void {
    const msg = err?.error?.message?.message;
    if (msg === 'Please login again. Your session is expired') {
      localStorage.clear();
      this.router.navigate(['/']);
    }
  }

  getStatusLabel(m: LocationMapping): string {
    if (m.ignored)    return 'Ignored';
    if (m.is_active)  return m.is_combination ? 'Active (combo)' : 'Active';
    if (!m.lw_location_id || !m.se_location_id) return 'Incomplete';
    return 'Inactive';
  }

  getStatusClass(m: LocationMapping): string {
    if (m.ignored)                          return 'badge-ignored';
    if (m.is_active && m.is_combination)    return 'badge-combo';
    if (m.is_active)                        return 'badge-active';
    if (!m.lw_location_id || !m.se_location_id) return 'badge-incomplete';
    return 'badge-inactive';
  }
}